/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Link } from 'carbon-components-react';
import _ from 'lodash';
import React, { useState, useEffect, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl, FormattedMessage } from 'react-intl';

import { useDelayDebounce } from '../../../../actions/hooks/useDelayDebounce';
import { fetchElevenLabsVoiceSettings } from '../../../api/speech';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { ControlledCheckbox } from '../../common/ControlledCheckbox';
import {
  ELEVEN_LABS_VOICE_SIMILARITY,
  ELEVEN_LABS_VOICE_SPEED,
  ELEVEN_LABS_VOICE_STABILITY,
  ELEVEN_LABS_VOICE_STYLE
} from '../constants';
import { links } from '../links';
import { FIELDS } from '../schema';
import { ControlledSliderVoiceSelector } from '../speech-common/ControlledSliderVoiceSelector';
import { TextToSpeech as messages } from './messages';
import './TextToSpeech.scss';

interface VoicesElevenLabsProps {
  selectedVoiceId: string;
}

export const VoicesElevenLabs = ({
  selectedVoiceId
}: VoicesElevenLabsProps) => {
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { getFieldValue, updateFieldValue } = useFormHelpers(formMethods);

  const voiceSettingsSaved = getFieldValue(
    FIELDS.TTS.ELEVENLABS_VOICE_SETTINGS_CONFIG
  );

  const [voiceSettings, setVoiceSettings] = useState(voiceSettingsSaved);

  // ElevenceAPIKey needs a debounce so that api request should not be go through immidiately and has a onBlur effect
  const debouncedElevenceAPIKey = useDelayDebounce(
    getFieldValue(FIELDS.TTS.ELEVENLABS_APIKEY),
    1000
  );

  useEffect(() => {
    if (
      debouncedElevenceAPIKey &&
      selectedVoiceId &&
      _.isEmpty(voiceSettingsSaved)
    ) {
      async function fetchData() {
        const voicesResponse = await fetchElevenLabsVoiceSettings(
          debouncedElevenceAPIKey,
          selectedVoiceId
        );

        setVoiceSettings(voicesResponse);
        updateFieldValue(
          FIELDS.TTS.ELEVENLABS_VOICE_SETTINGS.SPEED,
          voicesResponse.speed
        );
        updateFieldValue(
          FIELDS.TTS.ELEVENLABS_VOICE_SETTINGS.STABILITY,
          voicesResponse.stability
        );
        updateFieldValue(
          FIELDS.TTS.ELEVENLABS_VOICE_SETTINGS.STYLE,
          voicesResponse.style
        );
        updateFieldValue(
          FIELDS.TTS.ELEVENLABS_VOICE_SETTINGS.SIMILARITY_BOOST,
          voicesResponse.similarity_boost
        );
        updateFieldValue(
          FIELDS.TTS.ELEVENLABS_VOICE_SETTINGS.USE_SPEAKER_BOOST,
          voicesResponse.use_speaker_boost
        );
      }
      fetchData();
    }
  }, [
    debouncedElevenceAPIKey,
    selectedVoiceId,
    updateFieldValue,
    voiceSettingsSaved
  ]);

  const handleSpeedSelection = useCallback(
    value => {
      updateFieldValue(FIELDS.TTS.ELEVENLABS_VOICE_SETTINGS.SPEED, value);
    },
    [updateFieldValue]
  );

  const handleStabilitySelection = useCallback(
    value => {
      updateFieldValue(
        FIELDS.TTS.ELEVENLABS_VOICE_SETTINGS.STABILITY,
        value / 100
      );
    },
    [updateFieldValue]
  );

  const handleStyleSelection = useCallback(
    value => {
      updateFieldValue(FIELDS.TTS.ELEVENLABS_VOICE_SETTINGS.STYLE, value);
    },
    [updateFieldValue]
  );

  const handleSimilaritySelection = useCallback(
    value => {
      updateFieldValue(
        FIELDS.TTS.ELEVENLABS_VOICE_SETTINGS.SIMILARITY_BOOST,
        value / 100
      );
    },
    [updateFieldValue]
  );

  const handleSpeakerBoostSelection = useCallback(
    value => {
      updateFieldValue(
        FIELDS.TTS.ELEVENLABS_VOICE_SETTINGS.USE_SPEAKER_BOOST,
        Boolean(value)
      );
    },
    [updateFieldValue]
  );

  const sliderMap = [
    {
      defaultValue: voiceSettings?.speed ?? 0,
      min: ELEVEN_LABS_VOICE_SPEED.MIN,
      max: ELEVEN_LABS_VOICE_SPEED.MAX,
      stepVal: ELEVEN_LABS_VOICE_SPEED.STEP,
      labelText: t(messages.tts11LabsVoiceSpeedSliderLabel),
      onChange: handleSpeedSelection
    },
    {
      defaultValue: voiceSettings?.stability
        ? voiceSettings?.stability * 100
        : 0,
      min: ELEVEN_LABS_VOICE_STABILITY.MIN,
      max: ELEVEN_LABS_VOICE_STABILITY.MAX,
      stepVal: ELEVEN_LABS_VOICE_STABILITY.STEP,
      labelText: t(messages.tts11LabsVoiceStabilitySliderLabel),
      onChange: handleStabilitySelection
    },
    {
      defaultValue: voiceSettings?.similarity_boost
        ? voiceSettings?.similarity_boost * 100
        : 0,
      min: ELEVEN_LABS_VOICE_SIMILARITY.MIN,
      max: ELEVEN_LABS_VOICE_SIMILARITY.MAX,
      stepVal: ELEVEN_LABS_VOICE_SIMILARITY.STEP,
      labelText: t(messages.tts11LabsVoiceSimilaritySliderLabel),
      onChange: handleSimilaritySelection
    },
    {
      defaultValue: voiceSettings?.style ?? 0,
      min: ELEVEN_LABS_VOICE_STYLE.MIN,
      max: ELEVEN_LABS_VOICE_STYLE.MAX,
      stepVal: ELEVEN_LABS_VOICE_STYLE.STEP,
      labelText: t(messages.tts11LabsVoiceStyleSliderLabel),
      onChange: handleStyleSelection
    }
  ];

  return (
    <>
      <h4 className="productive-heading-02">
        {t(messages.customizeVoiceTitleElevenLabs)}
      </h4>
      <p className="TabContent__subtitle">
        <FormattedMessage
          {...messages.customizeVoiceDescriptionElevenLabs}
          values={{
            link: chunks => (
              <Link
                target="_blank"
                rel="noopener noreferrer"
                href={links.elevenLabsVoiceSettings}>
                {chunks}
              </Link>
            )
          }}
        />
      </p>
      <div className="TextToSpeech__field-container">
        {sliderMap.map(
          ({ defaultValue, labelText, min, max, stepVal, onChange }) => {
            return (
              <ControlledSliderVoiceSelector
                key={labelText}
                className="TextToSpeech__voice-sliders"
                defaultValue={defaultValue}
                id={`tts-${labelText}`}
                instanceLoaded={true}
                labelText={labelText}
                maxValue={max}
                minValue={min}
                onChange={onChange}
                stepValue={stepVal}
              />
            );
          }
        )}
        <div className="TabContent__spacer16" />
        <ControlledCheckbox
          field={FIELDS.TTS.ELEVENLABS_VOICE_SETTINGS.USE_SPEAKER_BOOST}
          labelText={t(messages.speakerBoost)}
          onChange={handleSpeakerBoostSelection}
        />
      </div>
    </>
  );
};
